// Cohort schedules are SQL DATE values, not instants. Keep their calendar
// fields intact; roster joined/last-active timestamps must not use this helper.
export function parseCalendarDate(value: unknown): Date | null {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(0, 0, 0, 0);
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
    ? date
    : null;
}

export function calendarDateInput(value: unknown): string {
  return parseCalendarDate(value) ? String(value) : '';
}

export function formatCalendarDate(value: unknown): string {
  const date = parseCalendarDate(value);
  return date
    ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date)
    : 'Date unavailable';
}

export function cohortSchedule(start: unknown, end: unknown): string {
  const startLabel = start ? formatCalendarDate(start) : 'Start not scheduled';
  return end ? `${startLabel} → ${formatCalendarDate(end)}` : `${startLabel} · No end date`;
}

export function calendarDaysUntil(value: unknown, now = new Date()): number | null {
  const date = parseCalendarDate(value);
  if (!date || !Number.isFinite(now.getTime())) return null;
  // Today is the viewer's calendar day. UTC is only an arithmetic container,
  // so DST transitions cannot turn one calendar day into zero or two days.
  const today = new Date(0);
  today.setUTCFullYear(now.getFullYear(), now.getMonth(), now.getDate());
  today.setUTCHours(0, 0, 0, 0);
  return Math.round((date.getTime() - today.getTime()) / 86_400_000);
}

export function cohortDateRangeError(start: unknown, end: unknown): string | null {
  if (start && !parseCalendarDate(start)) return 'Choose a valid start date.';
  if (end && !parseCalendarDate(end)) return 'Choose a valid end date, or leave it blank.';
  if (start && end && String(end) < String(start)) return 'End date cannot be before start date.';
  return null;
}

// The backend rejects is_active=false (archived), not derived schedule states.
// Planned and completed cohorts can still be active enrollment containers.
export function cohortAllowsEnrollment(status: unknown): boolean {
  return status === 'planned' || status === 'active' || status === 'completed';
}
